import { useMemo } from "react";
import { useAccountPref } from "@heraion/ui";

import { useAccountKey } from "./auth/useAccountKey";

// Web-rnw experience model — the console mirror of mobile's useExperience
// (apps/mobile/src/experience). A role-gated "experience" is a distinct
// surface set: athlete (everyone's default), coach (the team console, CO-8),
// and admin (the feedback inbox). The role switch renders only for multi-role
// users; single-role users never see it. Coach appears once the caller has ≥1
// active coach edge (they coach someone). Presentation lives in the account
// menu (AccountMenu) now — this module is pure state.
export type Experience = "athlete" | "admin" | "coach";

// Per account, as on mobile: one browser, two accounts, two choices.
const STORAGE_KEY_BASE = "jl.experience.v1";
const parseExperience = (v: string): Experience | null =>
  v === "athlete" || v === "admin" || v === "coach" ? v : null;

export function useExperience(isAdmin: boolean, isCoach: boolean, ready = true) {
  const available = useMemo<Experience[]>(() => {
    const xs: Experience[] = ["athlete"];
    if (isCoach) xs.push("coach");
    if (isAdmin) xs.push("admin");
    return xs;
  }, [isAdmin, isCoach]);

  // The persisted choice, restored on mount.
  const [stored, setActive] = useAccountPref(
    useAccountKey(STORAGE_KEY_BASE),
    parseExperience,
    "athlete",
  );

  // Never strand the user on an experience they can't see (e.g. revoked
  // admin, or a persisted 'admin' on a non-admin device): athlete is shown,
  // the stored choice stays.
  //
  // Gated on `ready` (CO-15): `isCoach` is resolved by an async edge read, so
  // until it lands `available` is just ['athlete'] and the fallback would hide
  // a persisted 'coach' choice on every refresh. Mobile's useExperience gates
  // the same fallback on ProfileContext's `loaded` for the same reason.
  const active: Experience = ready && !available.includes(stored) ? "athlete" : stored;

  return { available, active, setActive };
}
